import './DocumentTabTenderStatus.scss';
import { FC, useId, useMemo } from 'react';
import { clsx } from 'clsx';
import { Link } from 'react-router';
import { RouteNames } from '@/router/router.types';
import DocumentBox from '@molecules/DocumentBox';
import type { IDocumentStatusBar } from '@models/document/IDocumentStatusBar';
import { holdingBranches } from '@/utils/tenderStatus';
import { buildHeadline, buildHolders, buildNotes, buildRoute } from './tenderStatusModel';
import TenderHolderRow from './TenderHolderRow';
import TenderRoute from './TenderRoute';

interface IDocumentTabTenderStatusProps {
    statusBar?: IDocumentStatusBar;
    /** «Сейчас», мс — для stories и тестов; по умолчанию момент отрисовки */
    now?: number;
}

/**
 * Вкладка «Статус тендера»: слева — где закупка сейчас и кто её держит (люди, задачи, сроки, блокировка), справа —
 * узкая колонка «Ход закупки» по уровням. Старый ответ сервера (только уровни) показывает этап и ход без людей.
 */
const DocumentTabTenderStatus: FC<IDocumentTabTenderStatusProps> = ({ statusBar, now }) => {
    const routeTitleId = useId();
    const view = useMemo(() => {
        if (!statusBar?.levels?.length) return undefined;
        const at = now ?? Date.now();
        const branches = holdingBranches(statusBar);
        return {
            headline: buildHeadline(statusBar, branches, at),
            holders: buildHolders(branches, at),
            notes: buildNotes(branches, at),
            route: buildRoute(statusBar, branches, at),
        };
    }, [statusBar, now]);

    if (!view) return null;
    const { headline, holders, notes, route } = view;

    let empty: string | undefined;
    if (statusBar?.showPeople === false) empty = 'Ответственные и сроки для этой конфигурации не показываются.';
    else if (statusBar?.branches && !holders.length && !notes.length && headline && !headline.finished)
        empty = 'Активных задач на этом этапе нет.';

    return (
        <div className="tender-status" data-testid="tender-status">
            <DocumentBox>
                <div className="tender-status__grid">
                    <section className="tender-status__now" aria-label="Сейчас">
                        {headline && (
                            <>
                                <h2 className="tender-status__stage">{headline.title}</h2>
                                {headline.sub && <p className="tender-status__sub">{headline.sub}</p>}
                            </>
                        )}
                        {holders.length > 0 && (
                            <ul className="tender-status__holders" aria-label="Кто держит закупку">
                                {holders.map((holder) => (
                                    <TenderHolderRow key={holder.key} holder={holder} />
                                ))}
                            </ul>
                        )}
                        {notes.map((note) => (
                            <p
                                key={note.key}
                                className={clsx('tender-status__note', `tender-status__note--${note.tone}`)}
                            >
                                <strong>{note.title}</strong>
                                {note.text && <span className="tender-status__quiet"> {note.text}</span>}
                                {note.docId && (
                                    <>
                                        {' '}
                                        <Link to={`${RouteNames.DOCUMENT.replace(':id', note.docId)}#routes`}>
                                            Открыть маршруты
                                        </Link>
                                    </>
                                )}
                            </p>
                        ))}
                        {empty && <p className="tender-status__empty">{empty}</p>}
                    </section>
                    <aside className="tender-status__route" aria-labelledby={routeTitleId}>
                        <h3 className="tender-status__route-title" id={routeTitleId}>
                            Ход закупки
                        </h3>
                        <TenderRoute levels={route} />
                    </aside>
                </div>
            </DocumentBox>
        </div>
    );
};

export default DocumentTabTenderStatus;
